// Shared validation utilities for measurement constraints

// Helper: Check if value is within constraint bounds for a dimension
export const isWithinBounds = (value, min, max) => {
  const withinMin = min === null || min === undefined || value >= min;
  const withinMax = max === null || max === undefined || value <= max;
  return withinMin && withinMax;
};

// Helper: Find constraints that match the given dimension value
export const findMatchingConstraints = (
  applicableConstraints,
  dimension,
  value
) => {
  if (!value) return [];
  return applicableConstraints.filter((constraint) => {
    const min = constraint[`min_${dimension}`];
    const max = constraint[`max_${dimension}`];
    return isWithinBounds(value, min, max);
  });
};

// Helper: Extract min/max range from constraints for a dimension
export const getRangeForDimension = (constraints, dimension) => {
  const values = constraints
    .map((c) => ({ min: c[`min_${dimension}`], max: c[`max_${dimension}`] }))
    .filter(({ min, max }) => min !== null || max !== null);

  const mins = values
    .map((v) => v.min)
    .filter((v) => v !== null && v !== undefined);
  const maxs = values
    .map((v) => v.max)
    .filter((v) => v !== null && v !== undefined);

  return {
    min: mins.length > 0 ? Math.min(...mins) : null,
    max: maxs.length > 0 ? Math.max(...maxs) : null,
  };
};

// Helper: Format validation message
export const formatRangeMessage = (
  dimensionLabel,
  min,
  max,
  contextDimension = null
) => {
  const context = contextDimension ? ` for this ${contextDimension}` : "";
  if (min !== null && max !== null) {
    return `${dimensionLabel} must be between ${min}" and ${max}"${context}`;
  } else if (min !== null) {
    return `${dimensionLabel} must be at least ${min}"${context}`;
  } else if (max !== null) {
    return `${dimensionLabel} must be at most ${max}"${context}`;
  }
  return null;
};

// Main validation function for a dimension
export const validateDimension = (
  applicableConstraints,
  formData,
  dimension
) => {
  if (!applicableConstraints.length) return null;

  const otherDimension = dimension === "width" ? "height" : "width";
  const otherValue = formData[otherDimension];
  const value = formData[dimension];
  const dimensionLabel = dimension === "height" ? "Height" : "Width";

  // When other dimension has a value, validate based on its constraints
  if (otherValue) {
    const constraintsForOther = findMatchingConstraints(
      applicableConstraints,
      otherDimension,
      otherValue
    );
    if (constraintsForOther.length === 0) return null;

    const range = getRangeForDimension(constraintsForOther, dimension);

    // If current dimension has a value, validate it
    if (value && !isWithinBounds(value, range.min, range.max)) {
      return formatRangeMessage(
        dimensionLabel,
        range.min,
        range.max,
        otherDimension
      );
    }

    // If empty, show hint
    if (!value) {
      return formatRangeMessage(
        dimensionLabel,
        range.min,
        range.max,
        otherDimension
      );
    }
  } else if (value) {
    // Only this dimension has a value, validate against all constraints
    const range = getRangeForDimension(applicableConstraints, dimension);
    if (!isWithinBounds(value, range.min, range.max)) {
      return formatRangeMessage(dimensionLabel, range.min, range.max);
    }
  }

  return null;
};
